/**
 * Cursor-origin hover fill for every button-like control in the theme: .btn,
 * [data-icon-close], the pill tabs in tabbed-products and the social wall,
 * the newsletter submit, the search overlay's pills, the hero carousel's
 * arrows and scroll cue, and the footer's social links.
 *
 * A single pair of delegated pointerover/pointerout listeners writes the
 * pointer's entry/exit position into --fill-x/--fill-y (percentages of the
 * target's own box) and toggles .is-hover-fill; base.css reads those custom
 * properties to grow/shrink a ::before layer (clip-path circle) from that
 * point, and to run the 360deg spin on [data-icon-close] elements.
 * The per-component stylesheets mirror the same ::before pattern for controls
 * that are buttons but deliberately not .btn (different shape, palette or
 * states). They differ only in the fill COLOUR and in which of the two :root
 * timing pairs they read: --btn-fill-* for the slow sweep, --icon-close-fill-*
 * for the small circular controls that have to answer immediately.
 *
 * Cards (.promo-tile) and the skip link are deliberately NOT in this list -
 * a cursor sweep across a card reads as decoration rather than as pressing a
 * control. .shortcut-card was excluded on the same grounds and has since been
 * added back at the owner's request: the shop-by-spec chips are a filter
 * console, and they already inverted to solid ink on hover like a button, so
 * they were reading as controls either way. Shopify's unbranded payment
 * button is also in the list now (it was not when this note was written).
 *
 * Disabled entirely under prefers-reduced-motion (html.has-cursor-fill is
 * the gate every relevant base.css rule checks) and skipped for
 * disabled/aria-disabled targets so it never touches sold-out buttons.
 */
/*
 * THE CONTRACT for anything added to HOVER_TARGET_SELECTOR below.
 *
 * Registering a selector here only makes motion.js add .is-hover-fill and set
 * --fill-x/--fill-y. The control's own stylesheet must supply four things, and
 * a missing one fails QUIETLY - the control looks almost right, which is why
 * these were found one at a time by a merchant hovering things:
 *
 *   1. a ::before fill layer at z-index -1
 *      (0 or auto paints ABOVE inline content and sweeps over the label)
 *   2. clip-path: circle(0% at var(--fill-x, 50%) var(--fill-y, 50%)) at rest
 *   3. clip-path: circle(150% ...) under html.has-cursor-fill .is-hover-fill
 *   4. a transition on BOTH - the resting rule AND the .is-hover-fill rule
 *
 * Point 4 is the one that keeps being missed. A transition runs according to
 * whichever rule matches the DESTINATION state, so declaring it only on
 * .is-hover-fill gives a control that sweeps in and then vanishes instantly on
 * the way out. That was the "Buy it now hover doesn't work" bug, and it took
 * three attempts to find because every attempt looked at the class instead of
 * the way back.
 *
 * Timing: --btn-fill-* (3s in / 0.5s out) for buttons and pills, but
 * --icon-close-fill-* (0.6s / 0.3s) for small circular icon controls. A 3s
 * sweep across a ROW of small icons leaves a visible trail as each lags the
 * last.
 *
 * All 16 selectors below were audited against this on 2026-08-15 and pass.
 * .shortcut-card (shop-by-spec chips) joined later and was audited against
 * the same four points - it uses the button timing, not the icon timing,
 * because the chips are large controls laid out in a sparse grid, not a
 * tight row of small icons.
 * The product page's share row (.share-row__link, .share-row__copy) joined
 * later on the icon timing: four 36px circles and a copy pill packed into one
 * line are the row this note's trail warning is about.
 * .featured-carousel__arrow joined on the icon timing too, and for the same
 * reason as .product-gallery__nav: it is a 40px circle carrying one glyph,
 * sitting beside its twin.
 */
(function () {
  var HOVER_TARGET_SELECTOR =
    '.btn, [data-icon-close], .tabbed-products__tab, .product-tabs__tab, .social-wall__tab, ' +
    '.shopify-payment-button__button--unbranded, ' +
    '.newsletter__submit, .search-overlay__pill, .hero-carousel__control, ' +
    '.hero-carousel__scroll-cue, .footer__social-link, .shortcut-card, ' +
    '.contact__social-link, .variant-picker__pill:not(.variant-picker__swatch), ' +
    '.product-gallery__nav, .product-gallery__rail-btn, .product-gallery__lightbox-nav, ' +
    '.share-row__link, .share-row__copy, .featured-carousel__arrow, ' +
    '.social-wall__follow-link, .social-wall__arrow, .guide-quiz__answer, ' +
    '.header__icons .header__icon-btn:not(.header__theme)';
  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  function isDisabled(el) {
    /* Shopify's dynamic checkout button carries aria-disabled="true" while
       their script initialises it, on a perfectly buyable product - measured
       still true three and a half seconds after load. Treating that as a real
       disabled state is what stopped the fill: BOTH the entry handler and the
       self-healing pass below skip disabled controls, so the button never
       filled on a first hover, and only came right once Shopify flipped the
       attribute and the pointer moved again. That is the "hover Add to cart,
       come back, and now it works" report - the other button was never
       involved, only the time it took to reach it.

       So aria-disabled is ignored on that one control. Its real disabled state
       is the `disabled` property, which is still honoured, and a sweep across
       a button that turns out to be unavailable is a cosmetic no-op - the
       click is Shopify's to refuse either way. */
    if (el.disabled) return true;
    if (el.classList.contains('shopify-payment-button__button')) return false;
    return el.getAttribute('aria-disabled') === 'true';
  }

  function setFillOrigin(el, event) {
    var rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var x = ((event.clientX - rect.left) / rect.width) * 100;
    var y = ((event.clientY - rect.top) / rect.height) * 100;
    el.style.setProperty('--fill-x', x + '%');
    el.style.setProperty('--fill-y', y + '%');
  }

  function syncMotionClass() {
    document.documentElement.classList.toggle('has-cursor-fill', !reduceMotionQuery.matches);
  }
  syncMotionClass();
  reduceMotionQuery.addEventListener('change', syncMotionClass);

  /* composedPath() rather than event.target, so a control that lives inside a
     shadow root is still found: the browser retargets `target` to the shadow
     HOST, and closest() from there can never see the button inside. Falls back
     to target where composedPath is unavailable. */
  function findTarget(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : null;
    if (path) {
      for (var i = 0; i < path.length; i++) {
        var node = path[i];
        if (node && node.matches && node.matches(HOVER_TARGET_SELECTOR)) return node;
      }
    }
    return event.target.closest ? event.target.closest(HOVER_TARGET_SELECTOR) : null;
  }

  document.addEventListener('pointerover', function (event) {
    if (reduceMotionQuery.matches) return;
    var target = findTarget(event);
    if (!target || isDisabled(target) || target.contains(event.relatedTarget)) return;
    setFillOrigin(target, event);
    target.classList.add('is-hover-fill');
  });

  /* Self-healing pass. pointerover fires ONCE on entry, so anything that
     removes .is-hover-fill afterwards leaves the control permanently unfilled
     until the pointer leaves and returns.

     That is not hypothetical: Shopify's dynamic checkout button is rendered by
     their own script after this file runs, and re-renders its contents on
     initialisation - which discards the class if the pointer was already over
     it. The reported symptom was exactly that, Buy it now never filling until
     some other interaction had happened first.

     Rather than guess which third-party re-render is responsible, this simply
     restores the class whenever the pointer is over a target that has lost it.
     It does no work in the common case: the class is already present, so this
     returns on the first condition. */
  document.addEventListener(
    'pointermove',
    function (event) {
      if (reduceMotionQuery.matches) return;
      var target = findTarget(event);
      if (!target || isDisabled(target)) return;
      if (target.classList.contains('is-hover-fill')) return;
      setFillOrigin(target, event);
      target.classList.add('is-hover-fill');
    },
    { passive: true }
  );

  document.addEventListener('pointerout', function (event) {
    var target = findTarget(event);
    if (!target || target.contains(event.relatedTarget)) return;
    setFillOrigin(target, event);
    target.classList.remove('is-hover-fill');
  });
})();

